import { motion } from "framer-motion";
import { Mail, Linkedin, Instagram, Facebook, MapPin, Phone } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useViewMode } from "../contexts/ViewModeContext";
import Logo from "../components/Logo";

const getFooterSections = (isClient) => {
  const baseProduct = [
    { label: "Features", href: "/#features" },
    { label: "How It Works", href: "/#how-it-works" },
    { label: "FAQ", href: "/#faq" },
  ];
  
  // Add Pricing link only for client view
  if (isClient) {
    baseProduct.push({ label: "Pricing", href: "/#pricing" });
  }
  
  return [
    {
      title: "Product",
      links: baseProduct,
    },
  {
    title: "Company",
    links: [
      { label: "About Us", href: "/#" },
      { label: "Careers", href: "/careers" },
      { label: "Contact", href: "mailto:support@pragma.cx" },
    ],
  },
];
};

const socialLinks = [
  { icon: Mail, href: "mailto:support@pragma.cx", label: "Email" },
  { icon: Linkedin, href: "https://www.linkedin.com/company/pragma-cx/", label: "LinkedIn" },
  { icon: Instagram, href: "https://instagram.com/pragma.cx", label: "Instagram" },
  { icon: Facebook, href: "https://www.facebook.com/PragmaCX", label: "Facebook" },
];

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const { viewMode } = useViewMode();
  const footerSections = getFooterSections(viewMode === 'client');

  const handleNavClick = (e, href) => {
    if (href.startsWith("/#") && href !== "/#") {
      e.preventDefault();
      const hash = href.substring(2);
      const element = document.querySelector("#" + hash);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    }
  };

  return (
    <footer className="bg-card/50 border-t border-border relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute bottom-0 left-0 w-96 h-96 border border-cyan-400/5 rounded-full -translate-x-1/2 translate-y-1/2" />

      <div className="container py-16">
        {/* Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Column */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
                <Logo size={36} animated={false} />
                <span className="font-mono font-bold text-foreground text-xl">
                  PRAGMA
                </span>
              </div>
            </div>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm">
              Elite customer service outsourcing. Deploy professional support
              agents in 14 business days. Save 60% compared to in-house teams.
            </p>

            {/* Contact Info */}
            <div className="space-y-2">
              <a
                href="mailto:support@pragma.cx"
                className="flex items-center gap-2 text-muted-foreground text-sm hover:text-cyan-400 transition-colors"
              >
                <Mail className="w-4 h-4" />
                support@pragma.cx
              </a>
            </div>
          </motion.div>

          {/* Footer Sections */}
          {footerSections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <h4 className="text-foreground font-mono font-bold text-sm uppercase tracking-wider mb-4">
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.links.map((link) => {
                  const isHash = link.href.startsWith("/#");
                  const isMailto = link.href.startsWith("mailto:");
                  
                  if (isMailto) {
                    return (
                      <li key={link.label}>
                        <a
                          href={link.href}
                          className="text-muted-foreground text-sm hover:text-cyan-400 transition-colors"
                        >
                          {link.label}
                        </a>
                      </li>
                    );
                  }
                  
                  return (
                    <li key={link.label}>
                      {isHash ? (
                        <a
                          href={link.href}
                          onClick={(e) => handleNavClick(e, link.href)}
                          className="text-muted-foreground text-sm hover:text-cyan-400 transition-colors"
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link
                          to={link.href}
                          className="text-muted-foreground text-sm hover:text-cyan-400 transition-colors"
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Divider */}
        <div className="border-t border-border pt-8" />

        {/* Bottom Section */}
        <motion.div
          className="flex flex-col md:flex-row items-center justify-between gap-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          {/* Copyright */}
          <p className="text-gray-500 text-sm">
            © {currentYear} Pragma. All rights reserved.
          </p>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => {
              const Icon = social.icon;
              return (
                <motion.a
                  key={social.label}
                  href={social.href}
                  className="p-2 text-muted-foreground hover:text-cyan-400 border border-border hover:border-cyan-400/50 transition-all"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  title={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <Icon className="w-4 h-4" />
                </motion.a>
              );
            })}
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
